import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

export default function EscrowSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const escrowId = params.get("escrow_id");
  const [tries, setTries] = useState(0);
  const [status, setStatus] = useState(null);
  const [escrow, setEscrow] = useState(null);
  const stopped = useRef(false);

  useEffect(() => {
    if (!sessionId || stopped.current) return;
    let canceled = false;
    const poll = async () => {
      if (canceled || tries >= 10) return;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        setStatus(r.data);
        if (r.data.payment_status === "paid") {
          stopped.current = true;
          const e = await api.get(`/escrow/${escrowId}`);
          setEscrow(e.data);
          return;
        }
      } catch (e) { /* keep retrying */ }
      setTimeout(() => setTries((t) => t + 1), 2500);
    };
    poll();
    return () => { canceled = true; };
  }, [sessionId, escrowId, tries]);

  const paid = status?.payment_status === "paid" || escrow?.status === "held" || escrow?.status === "released";

  return (
    <div className="max-w-2xl mx-auto px-4 py-16" data-testid="page-escrow-success">
      <div className="border border-border p-10 text-center space-y-5">
        {paid ? (
          <>
            <ShieldCheck className="w-12 h-12 mx-auto text-primary" />
            <div className="font-overline text-accent">Escrow Funded</div>
            <h1 className="font-display text-3xl">Your funds are held safely</h1>
            <p className="text-sm text-muted-foreground">
              ${(escrow?.amount || status?.amount_total / 100 || 0).toLocaleString()} is now in escrow.
              Our admin will release it to the seller once the note transfer is verified — or refund you in full if the deal falls through.
            </p>
            <div className="font-mono-data text-xs text-muted-foreground" data-testid="escrow-id-display">
              {escrowId}
            </div>
            <Link to="/dashboard" className="inline-block text-sm underline">Back to dashboard</Link>
          </>
        ) : tries >= 10 ? (
          <>
            <AlertTriangle className="w-10 h-10 mx-auto text-amber-600" />
            <div className="font-overline text-accent">Payment still processing</div>
            <p className="text-sm text-muted-foreground">
              Your payment is taking longer than usual to confirm. Refresh this page in a minute, or check your dashboard.
            </p>
            <Link to="/dashboard" className="inline-block text-sm underline">Back to dashboard</Link>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-primary" />
            <div className="font-overline text-accent">Confirming payment…</div>
            <p className="text-sm text-muted-foreground">Hold tight while we verify with Stripe.</p>
          </>
        )}
      </div>
    </div>
  );
}
